import dayjs from 'dayjs';
import { getWindDirection } from './index';

/**
 * Determines flight safety status for a takeoff site based on weather data
 * @param {Object} site - Takeoff site object with suitableWindDirections
 * @param {Object} weatherData - Current weather data
 * @returns {Object} - { status, isOffline, isSuitableWind, isWindTooStrong }
 */
export const getFlightSafetyStatus = (site, weatherData) => {
    // Check if station is offline
    if (!weatherData || !weatherData.SEND_TIME) {
        return {
            status: 'offline',
            isOffline: true,
            isSuitableWind: false,
            isWindTooStrong: false
        };
    }

    const lastUpdate = dayjs(weatherData.SEND_TIME);
    const fiveMinutesAgo = dayjs().subtract(5, "minutes");

    if (lastUpdate.isBefore(fiveMinutesAgo)) {
        return {
            status: 'offline',
            isOffline: true,
            isSuitableWind: false,
            isWindTooStrong: false
        };
    }

    // Check wind conditions for flying
    if (weatherData.WIND_SP !== undefined) {
        const windSpeed = parseFloat(weatherData.WIND_SP);
        const gustSpeed = parseFloat(weatherData.WIND_GST || 0);
        const isUnsafeWind = windSpeed > 7 || gustSpeed > 7;

        if (isUnsafeWind) {
            return {
                status: 'danger',
                isOffline: false,
                isSuitableWind: false,
                isWindTooStrong: true
            };
        }

        // Check if wind direction is suitable for flying
        if (weatherData.WIND_ANG && site?.suitableWindDirections) {
            const currentWindDirection = getWindDirection(weatherData.WIND_ANG);
            const isSuitable = site.suitableWindDirections.includes(currentWindDirection);

            if (isSuitable) {
                return {
                    status: 'good',
                    isOffline: false,
                    isSuitableWind: true,
                    isWindTooStrong: false
                };
            }
        }
    }

    // Default neutral status
    return {
        status: 'neutral',
        isOffline: false,
        isSuitableWind: false,
        isWindTooStrong: false
    };
};

/**
 * Gets the appropriate status label for translation
 * @param {string} status - Status from getFlightSafetyStatus
 * @returns {string} - Translation key
 */
export const getFlightStatusLabel = (status) => {
    switch (status) {
        case 'offline':
            return 'Station Offline';
        case 'danger':
            return 'Strong Wind';
        case 'good':
            return 'Good Wind';
        case 'neutral':
        default:
            return 'Check Conditions';
    }
};

/**
 * Gets CSS classes for border styling based on flight status
 * @param {string} status - Status from getFlightSafetyStatus
 * @returns {string} - Tailwind CSS classes
 */
export const getFlightStatusBorderClasses = (status) => {
    switch (status) {
        case 'offline':
            return 'border-red-200 dark:border-red-800';
        case 'danger':
            return 'border-orange-400 dark:border-orange-500 shadow-orange-100 dark:shadow-orange-900/20';
        case 'good':
            return 'border-green-400 dark:border-green-500 shadow-green-100 dark:shadow-green-900/20';
        case 'neutral':
        default:
            return 'border-gray-200 dark:border-slate-700';
    }
};

/**
 * Gets CSS classes for badge styling based on flight status
 * @param {string} status - Status from getFlightSafetyStatus
 * @returns {string} - Tailwind CSS classes
 */
export const getFlightStatusBadgeClasses = (status) => {
    switch (status) {
        case 'offline':
            return 'bg-red-500/90';
        case 'danger':
            return 'bg-orange-500/90';
        case 'good':
            return 'bg-green-500/90';
        case 'neutral':
        default:
            return 'bg-gray-500/90';
    }
};
